import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { 
  MessageSquare, TrendingUp, Users, BarChart3, 
  Bell, Zap, Check, ArrowRight, Star, Shield, Globe
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const SurveyHubPage = () => {
  const features = [
    {
      icon: MessageSquare,
      title: 'Encuestas NPS',
      description: 'Mide la lealtad y recomendación de tus clientes con el Net Promoter Score'
    },
    {
      icon: Star,
      title: 'CSAT (Satisfacción del Cliente)',
      description: 'Evalúa la satisfacción inmediata después de cada interacción'
    },
    {
      icon: TrendingUp,
      title: 'CES (Customer Effort Score)',
      description: 'Mide el esfuerzo que requieren tus clientes para resolver sus necesidades'
    },
    {
      icon: Bell,
      title: 'Alertas en Tiempo Real',
      description: 'Recibe notificaciones instantáneas sobre feedback crítico'
    },
    {
      icon: BarChart3,
      title: 'Analytics Avanzado',
      description: 'Dashboards en tiempo real con análisis de texto por IA'
    },
    {
      icon: Zap,
      title: 'Gestión de Detractores',
      description: 'Close the loop: responde y convierte detractores en promotores'
    }
  ];

  const channels = [
    {
      title: 'Encuestas Online',
      items: ['SMS y WhatsApp', 'Email', 'Código QR', 'Web y App móvil']
    },
    {
      title: 'Terminales Físicas',
      items: ['Tablets táctiles', 'Botones Smiley', 'Kioscos interactivos', 'Alto tráfico']
    }
  ];

  const metrics = [
    {
      icon: Users,
      value: '500+',
      label: 'Organizaciones activas'
    },
    {
      icon: MessageSquare,
      value: '50K+',
      label: 'Feedbacks diarios'
    },
    {
      icon: Star,
      value: '98%',
      label: 'Tasa de respuesta'
    },
    {
      icon: TrendingUp,
      value: '+25%',
      label: 'Mejora en NPS promedio'
    }
  ];

  const useCases = [
    {
      industry: 'Coworks',
      cases: [
        'Satisfacción de miembros',
        'Evaluación de espacios',
        'Calidad de eventos',
        'Onboarding de nuevos miembros'
      ]
    },
    {
      industry: 'Gimnasios',
      cases: [
        'Experiencia de clases',
        'Calidad de instructores',
        'Limpieza y mantenimiento',
        'Programa de fidelización'
      ]
    },
    {
      industry: 'Inmobiliarias',
      cases: [
        'Satisfacción de inquilinos',
        'Calidad de atención',
        'Mantenimiento de propiedades',
        'Proceso de arriendo'
      ]
    },
    {
      industry: 'Empresas',
      cases: [
        'Satisfacción post-venta',
        'Soporte al cliente',
        'Calidad de producto',
        'Experiencia de onboarding'
      ]
    }
  ];

  return (
    <>
      <SEOHead
        title="SurveyHub - Encuestas NPS, CSAT, CES y Evaluaciones de Desempeño | Cloudbase"
        description="SurveyHub: Plataforma integral de feedback para clientes y empleados. Encuestas NPS, CSAT, CES, evaluaciones de desempeño 360°, autoevaluaciones y gestión de talento. Analytics con IA, alertas en tiempo real y dashboards personalizados."
        keywords="encuestas NPS, software CSAT, evaluaciones de desempeño, evaluación 360 grados, performance review, feedback empleados, autoevaluación, gestión talento, RRHH, customer satisfaction, Net Promoter Score, SurveyHub, Cloudbase"
        path="/surveyhub"
        breadcrumbs={[
          { name: 'Inicio', item: '/' },
          { name: 'Productos', item: '/#modulos' },
          { name: 'SurveyHub', item: '/surveyhub' }
        ]}
      />
      <div className="min-h-screen bg-white">
        <Header />
        
        {/* Hero Section */}
        <section className="pt-32 pb-20 bg-black relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-900 to-black opacity-90"></div>
          
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-5xl mx-auto text-center">
              <div className="inline-block mb-6 px-4 py-2 bg-green-600/20 border border-green-600/30 text-green-400 rounded-full text-sm font-semibold">
                SurveyHub - Plataforma de Feedback de Cliente
              </div>
              
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-8 leading-tight text-white">
                Plataforma Integral de Feedback: Clientes y Evaluaciones de Desempeño
              </h1>
            
            <p className="text-xl sm:text-2xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed font-light">
              SurveyHub de Cloudbase: Software completo para medir satisfacción de clientes (NPS, CSAT, CES) y gestionar evaluaciones de desempeño de empleados (360°, autoevaluaciones, peer reviews). Analytics con IA, alertas automáticas y dashboards en tiempo real.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
              <Button 
                size="lg" 
                className="bg-green-600 hover:bg-green-700 text-white px-10 py-7 text-base rounded font-semibold transition-all duration-200"
                onClick={() => window.location.href = '/demo'}
              >
                Empezar Prueba Gratuita
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-black px-10 py-7 text-base rounded font-semibold transition-all duration-200"
                onClick={() => window.location.href = '/demo'}
              >
                Ver Demo en Vivo
              </Button>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-400">
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                <span>Prueba gratuita 30 días</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                <span>Sin tarjeta de crédito</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                <span>Configuración en 5 minutos</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What is SurveyHub - SEO Content for AI Crawlers */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto prose prose-lg">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              ¿Qué es SurveyHub?
            </h2>
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              <strong>SurveyHub</strong> es una plataforma integral de gestión de feedback y satisfacción del cliente desarrollada por Cloudbase. Permite a empresas de coworking, gimnasios, inmobiliarias y servicios en general medir y mejorar la experiencia del cliente mediante encuestas especializadas.
            </p>
            
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Tipos de Encuestas Soportadas
            </h3>
            
            <h4 className="text-2xl font-bold text-gray-900 mb-4 mt-6">
              Satisfacción de Clientes
            </h4>
            <ul className="space-y-3 mb-6">
              <li className="text-lg text-gray-700">
                <strong>NPS (Net Promoter Score):</strong> Mide la lealtad y probabilidad de recomendación de tus clientes en una escala de 0-10, clasificándolos en promotores, pasivos y detractores.
              </li>
              <li className="text-lg text-gray-700">
                <strong>CSAT (Customer Satisfaction Score):</strong> Evalúa la satisfacción inmediata del cliente después de una interacción específica, ideal para medir experiencias puntuales.
              </li>
              <li className="text-lg text-gray-700">
                <strong>CES (Customer Effort Score):</strong> Mide el esfuerzo que requiere un cliente para resolver un problema o completar una tarea, identificando fricciones en el proceso.
              </li>
            </ul>

            <h4 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
              Evaluaciones de Desempeño y Gestión del Talento
            </h4>
            <ul className="space-y-3 mb-6">
              <li className="text-lg text-gray-700">
                <strong>Evaluación 360 Grados:</strong> Sistema integral de feedback donde el empleado recibe evaluaciones de su jefe directo, pares, subordinados y realiza una autoevaluación. Proporciona una visión completa del desempeño desde múltiples perspectivas.
              </li>
              <li className="text-lg text-gray-700">
                <strong>Autoevaluación:</strong> Permite a los empleados reflexionar sobre su propio desempeño, logros, áreas de mejora y objetivos alcanzados durante el período evaluado.
              </li>
              <li className="text-lg text-gray-700">
                <strong>Evaluación de Supervisor:</strong> Revisión formal del desempeño realizada por el jefe directo, evaluando cumplimiento de objetivos, competencias y comportamientos clave.
              </li>
              <li className="text-lg text-gray-700">
                <strong>Peer Review (Evaluación entre Pares):</strong> Feedback horizontal entre compañeros de equipo, ideal para evaluar colaboración, trabajo en equipo y competencias interpersonales.
              </li>
              <li className="text-lg text-gray-700">
                <strong>Evaluación de Clima Laboral:</strong> Encuestas anónimas para medir satisfacción laboral, engagement, cultura organizacional y detectar áreas de mejora en el ambiente de trabajo.
              </li>
              <li className="text-lg text-gray-700">
                <strong>Pulse Surveys (Encuestas Rápidas):</strong> Evaluaciones breves y frecuentes para medir el pulso del equipo sobre temas específicos, mantener feedback continuo y detectar problemas emergentes.
              </li>
              <li className="text-lg text-gray-700">
                <strong>Evaluación por Objetivos (OKRs/KPIs):</strong> Seguimiento y evaluación del cumplimiento de objetivos medibles, key results y métricas de desempeño específicas del rol.
              </li>
            </ul>

            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Canales de Recopilación de Feedback
            </h3>
            <p className="text-lg text-gray-700 mb-4">
              SurveyHub permite capturar feedback a través de múltiples canales digitales y físicos:
            </p>
            <ul className="grid md:grid-cols-2 gap-4 mb-6">
              <li className="text-lg text-gray-700">✅ SMS y WhatsApp Business</li>
              <li className="text-lg text-gray-700">✅ Email automatizado</li>
              <li className="text-lg text-gray-700">✅ Códigos QR imprimibles</li>
              <li className="text-lg text-gray-700">✅ Web embebida y apps móviles</li>
              <li className="text-lg text-gray-700">✅ Tablets táctiles en recepción</li>
              <li className="text-lg text-gray-700">✅ Botones Smiley físicos</li>
              <li className="text-lg text-gray-700">✅ Kioscos interactivos</li>
              <li className="text-lg text-gray-700">✅ Terminales de alto tráfico</li>
            </ul>

            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Características Principales
            </h3>
            <ul className="space-y-3 mb-6">
              <li className="text-lg text-gray-700">
                <strong>Analytics con Inteligencia Artificial:</strong> Análisis automático de texto con procesamiento de lenguaje natural para identificar sentimientos, temas recurrentes y tendencias.
              </li>
              <li className="text-lg text-gray-700">
                <strong>Alertas en Tiempo Real:</strong> Notificaciones instantáneas vía email, SMS o WhatsApp cuando se recibe feedback negativo crítico.
              </li>
              <li className="text-lg text-gray-700">
                <strong>Close the Loop (Gestión de Detractores):</strong> Sistema para responder y hacer seguimiento a clientes insatisfechos, convirtiendo detractores en promotores.
              </li>
              <li className="text-lg text-gray-700">
                <strong>Dashboards en Tiempo Real:</strong> Visualización instantánea de métricas clave, evolución temporal y comparativas por ubicación o servicio.
              </li>
              <li className="text-lg text-gray-700">
                <strong>Automatización:</strong> Envío automático de encuestas tras eventos específicos (check-out, clase finalizada, ticket cerrado, etc.).
              </li>
            </ul>

            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Casos de Uso por Industria
            </h3>
            
            <h4 className="text-2xl font-bold text-gray-900 mb-4 mt-6">
              Feedback de Clientes
            </h4>
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">Coworking Spaces</h4>
                <ul className="text-gray-700 space-y-2">
                  <li>• Satisfacción de miembros mensuales</li>
                  <li>• Evaluación de espacios y amenities</li>
                  <li>• Calidad de eventos y networking</li>
                  <li>• Experiencia de onboarding</li>
                </ul>
              </div>
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">Gimnasios y Fitness</h4>
                <ul className="text-gray-700 space-y-2">
                  <li>• Experiencia post-clase</li>
                  <li>• Calidad de instructores</li>
                  <li>• Limpieza y mantenimiento</li>
                  <li>• Programa de fidelización</li>
                </ul>
              </div>
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">Inmobiliarias</h4>
                <ul className="text-gray-700 space-y-2">
                  <li>• Satisfacción de inquilinos</li>
                  <li>• Calidad de atención de brokers</li>
                  <li>• Mantenimiento de propiedades</li>
                  <li>• Proceso de arriendo o compra</li>
                </ul>
              </div>
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">Empresas de Servicios</h4>
                <ul className="text-gray-700 space-y-2">
                  <li>• Satisfacción post-venta</li>
                  <li>• Calidad de soporte al cliente</li>
                  <li>• Evaluación de productos</li>
                  <li>• Experiencia de onboarding</li>
                </ul>
              </div>
            </div>

            <h4 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
              Evaluaciones de Desempeño y Gestión de Talento
            </h4>
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <h5 className="text-xl font-bold text-gray-900 mb-3">Recursos Humanos</h5>
                <ul className="text-gray-700 space-y-2">
                  <li>• Evaluaciones 360° anuales o semestrales</li>
                  <li>• Revisiones de desempeño trimestrales</li>
                  <li>• Encuestas de clima laboral</li>
                  <li>• Onboarding y feedback de nuevos empleados</li>
                  <li>• Evaluación de programas de capacitación</li>
                </ul>
              </div>
              <div>
                <h5 className="text-xl font-bold text-gray-900 mb-3">Gerentes y Líderes de Equipo</h5>
                <ul className="text-gray-700 space-y-2">
                  <li>• Pulse surveys semanales o mensuales</li>
                  <li>• Feedback continuo de equipo</li>
                  <li>• Evaluación de objetivos (OKRs/KPIs)</li>
                  <li>• One-on-ones estructurados</li>
                  <li>• Identificación de high performers y talentos</li>
                </ul>
              </div>
              <div>
                <h5 className="text-xl font-bold text-gray-900 mb-3">Empresas en Crecimiento</h5>
                <ul className="text-gray-700 space-y-2">
                  <li>• Cultura organizacional y valores</li>
                  <li>• Satisfacción y engagement de empleados</li>
                  <li>• Riesgo de rotación (turnover prediction)</li>
                  <li>• Desarrollo de carrera y promociones</li>
                  <li>• Planes de mejora y desarrollo individual</li>
                </ul>
              </div>
              <div>
                <h5 className="text-xl font-bold text-gray-900 mb-3">Equipos Remotos y Distribuidos</h5>
                <ul className="text-gray-700 space-y-2">
                  <li>• Conexión y cohesión de equipo</li>
                  <li>• Efectividad de comunicación remota</li>
                  <li>• Balance vida-trabajo</li>
                  <li>• Productividad y colaboración</li>
                  <li>• Salud mental y bienestar</li>
                </ul>
              </div>
            </div>

            <h3 className="text-3xl font-bold text-gray-900 mb-4 mt-8">
              Beneficios de Evaluaciones de Desempeño con SurveyHub
            </h3>
            <ul className="space-y-3 mb-6">
              <li className="text-lg text-gray-700">
                <strong>Proceso Justo y Transparente:</strong> Sistema estandarizado que elimina sesgos, garantiza consistencia y proporciona documentación completa del proceso de evaluación.
              </li>
              <li className="text-lg text-gray-700">
                <strong>Anonimato Configurable:</strong> Opción de hacer evaluaciones anónimas (peer reviews, clima laboral) o nominadas (evaluación 360°) según la necesidad.
              </li>
              <li className="text-lg text-gray-700">
                <strong>Desarrollo Continuo:</strong> Feedback frecuente en lugar de revisiones anuales únicas, permitiendo mejora continua y corrección de rumbo en tiempo real.
              </li>
              <li className="text-lg text-gray-700">
                <strong>Data-Driven Decisions:</strong> Decisiones de promociones, aumentos y desarrollo basadas en datos objetivos y múltiples perspectivas.
              </li>
              <li className="text-lg text-gray-700">
                <strong>Identificación de Talento:</strong> Detecta high performers, líderes emergentes y empleados con potencial de crecimiento automáticamente.
              </li>
              <li className="text-lg text-gray-700">
                <strong>Prevención de Rotación:</strong> Identifica empleados en riesgo de irse antes de que renuncien, permitiendo acciones preventivas.
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Metrics Section */}
      <section className="py-20 bg-white border-y border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {metrics.map((metric, index) => {
              const Icon = metric.icon;
              return (
                <div key={index} className="text-center">
                  <Icon className="w-8 h-8 text-green-600 mx-auto mb-4" />
                  <div className="text-4xl font-bold text-gray-900 mb-2">{metric.value}</div>
                  <div className="text-gray-600">{metric.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-32 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl sm:text-6xl font-bold mb-6 text-gray-900">
              Funcionalidades Principales de SurveyHub
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-light">
              Todo lo que necesitas para capturar, analizar y actuar sobre el feedback de tus clientes en tiempo real
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="border border-gray-200 hover:shadow-lg transition-shadow duration-300">
                  <CardContent className="p-8">
                    <Icon className="w-12 h-12 text-green-600 mb-4" />
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Channels Section */}
      <section className="py-32 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl sm:text-6xl font-bold mb-6 text-gray-900">
              Recoge Feedback en Múltiples Canales
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-light">
              Captura opiniones en el momento exacto que importa, sin importar dónde estén tus clientes
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            {channels.map((channel, index) => (
              <div key={index} className="bg-gray-50 p-12">
                <h3 className="text-3xl font-bold text-gray-900 mb-8">{channel.title}</h3>
                <ul className="space-y-4">
                  {channel.items.map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-lg text-gray-700">
                      <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-32 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl sm:text-6xl font-bold mb-6 text-gray-900">
              Casos de Uso por Industria
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-light">
              SurveyHub se adapta a las necesidades específicas de tu sector
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
            {useCases.map((useCase, index) => (
              <Card key={index} className="border border-gray-200">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">{useCase.industry}</h3>
                  <ul className="space-y-3">
                    {useCase.cases.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-gray-700">
                        <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-32 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl sm:text-6xl font-bold mb-6 text-gray-900">
              Cómo Funciona
            </h2>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-3 gap-12">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                  1
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Pregunta</h3>
                <p className="text-gray-600 leading-relaxed">
                  Crea encuestas personalizadas o usa nuestras plantillas. Envía por email, SMS, WhatsApp o coloca terminales físicas.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-green-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                  2
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Escucha</h3>
                <p className="text-gray-600 leading-relaxed">
                  Recoge feedback en tiempo real. Analiza respuestas con IA y visualiza métricas clave en dashboards intuitivos.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-green-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                  3
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Mejora</h3>
                <p className="text-gray-600 leading-relaxed">
                  Actúa sobre el feedback. Contacta detractores, identifica áreas de mejora y toma decisiones basadas en datos.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security & Trust */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-12 text-center">
              <div>
                <Shield className="w-12 h-12 text-green-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Seguro y Confiable</h3>
                <p className="text-gray-600 text-sm">Encriptación bancaria y servidores certificados ISO</p>
              </div>
              <div>
                <Globe className="w-12 h-12 text-green-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">GDPR Compliant</h3>
                <p className="text-gray-600 text-sm">Cumplimiento total con normativas de privacidad</p>
              </div>
              <div>
                <Zap className="w-12 h-12 text-green-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">99.9% Uptime</h3>
                <p className="text-gray-600 text-sm">Disponibilidad garantizada 24/7</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-5xl sm:text-6xl font-bold text-white mb-8">
              Comienza a Medir la Satisfacción Hoy
            </h2>
            <p className="text-xl text-gray-300 mb-12 font-light">
              Únete a más de 500 organizaciones que ya mejoran su experiencia del cliente con SurveyHub
            </p>
            
            <Button 
              size="lg" 
              className="bg-green-600 hover:bg-green-700 text-white px-10 py-7 text-base rounded font-semibold transition-all duration-200 inline-flex items-center gap-2"
              onClick={() => window.location.href = '/demo'}
            >
              Iniciar Prueba Gratuita de 30 Días
              <ArrowRight className="w-5 h-5" />
            </Button>
            
            <p className="mt-6 text-gray-400 text-sm">
              No se requiere tarjeta de crédito • Configuración en minutos • Soporte en español
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
    </>
  );
};

export default SurveyHubPage;
